import { type FC } from "react";

import { Card, CardRowSimple, type GridCard } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type Gene, type Region, type Variant } from "../../../graphql.js";
import { ContigTile, GeneTile, GenomeViewTile, VariantTile } from "../../core/tiles/index.js";

export interface IContigLocationCardProps extends GridCard {
  label?: string;
  region: Region;
  lowerNeighborGenes?: Gene[];
  higherNeighborGenes?: Gene[];
  lowerNeighborVariants?: Variant[];
  higherNeighborVariants?: Variant[];
  variant?: Variant; // specific info for genome view context
}

const ContigLocationCard: FC<IContigLocationCardProps> = (props) => {
  const region = props.region;
  const contig = region.contig;
  const startFormatted = formatNumberByThousands(region.range.start);
  const stopFormatted = formatNumberByThousands(region.range.stop);
  const baseGenomeViewUrl = props.variant ? `/variant/${props.variant.mutId}` : `/contig/${contig.id}`;

  return (
    <Card key="genome-info-card" title={props.label ?? "Location"}>
      <CardRowSimple label={"Contig"}>
        <ContigTile contig={contig} />
        <GenomeViewTile
          title={`${startFormatted} – ${stopFormatted} (${region.strand})`}
          // TODO Range is not mandatory and the GenomeView of an entity is automatically set.
          to={`${baseGenomeViewUrl}/genome?range=${region.range.start},${region.range.stop}`}
        >
          {formatNumberByThousands(region.range.stop - region.range.start + 1)} bp
        </GenomeViewTile>
      </CardRowSimple>
      <CardRowSimple label={"Neighbor genes"}>
        {props.lowerNeighborGenes?.map((gene) => (
          <GeneTile gene={gene} footNote={"Lower"} key={`neighbor-gene-${gene.id}`} />
        ))}
        {props.higherNeighborGenes?.map((gene) => (
          <GeneTile gene={gene} footNote={"Higher"} key={`neighbor-gene-${gene.id}`} />
        ))}
      </CardRowSimple>
      <CardRowSimple label={"Neighbor variants"}>
        {props.lowerNeighborVariants?.map((variant) => (
          <VariantTile variant={variant} footNote={"Lower"} key={`neighbor-gene-${variant.id}`} />
        ))}
        {props.higherNeighborVariants?.map((variant) => (
          <VariantTile variant={variant} footNote={"Higher"} key={`neighbor-gene-${variant.id}`} />
        ))}
      </CardRowSimple>
    </Card>
  );
};

export { ContigLocationCard };
